import type {
  MetaFunction,
  LoaderFunctionArgs,
  ActionFunctionArgs,
} from '@remix-run/node'
import { useRef, useEffect } from 'react'
import { Form, useLoaderData, useNavigation } from '@remix-run/react'
import { json } from '@remix-run/node'
import { useHydrated } from 'remix-utils/use-hydrated'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { Loader2 } from 'lucide-react'
import { authenticator } from '#app/modules/auth/auth.server'
import { getSession, commitSession } from '#app/modules/auth/auth-session.server'
import { validateCSRF } from '#app/utils/csrf.server'
import { checkHoneypot } from '#app/utils/honeypot.server'
import { useIsPending } from '#app/utils/misc'
import { siteConfig } from '#app/utils/constants/brand'
import { Input } from '#app/components/ui/input'
import { Button } from '#app/components/ui/button'
import { ROUTE_PATH as DASHBOARD_PATH } from '#app/routes/dashboard+/_layout'
import { ROUTE_PATH as AUTH_VERIFY_PATH } from '#app/routes/auth+/verify'

export const ROUTE_PATH = '/auth/login' as const

export const LoginSchema = z.object({
  email: z.string().max(256).email('Email address is not valid.'),
})

export const meta: MetaFunction = () => {
  return [{ title: `${siteConfig.siteTitle} - Login` }]
}

export async function loader({ request }: LoaderFunctionArgs) {
  await authenticator.isAuthenticated(request, {
    successRedirect: DASHBOARD_PATH,
  })

  const cookie = await getSession(request.headers.get('Cookie'))
  const authEmail = cookie.get('auth:email')
  const authError = cookie.get(authenticator.sessionErrorKey)

  const safeAuthError = authError && typeof authError === 'object' && 'message' in authError
    ? authError
    : { message: '' }

  return json({ 
    authEmail, 
    authError: safeAuthError 
  } as const, {
    headers: {
      'Set-Cookie': await commitSession(cookie),
    },
  })
}

export async function action({ request }: ActionFunctionArgs) {
  const url = new URL(request.url)
  const pathname = url.pathname
  
  try {
    const clonedRequest = request.clone()
    const formData = await clonedRequest.formData()
    await validateCSRF(formData, clonedRequest.headers)
    checkHoneypot(formData)
    
    // The authenticate call is throwing a Response object
    const response = await authenticator.authenticate('TOTP', request, {
      successRedirect: AUTH_VERIFY_PATH,
      failureRedirect: pathname,
    })
    return response
  } catch (error) {
    if (error instanceof Response) {
      // If it's a redirect response, return it
      return error
    }
    
    console.error('Authentication error:', error)
    return json(
      { error: 'Authentication failed' },
      { status: 400 }
    )
  }
}

export default function Login() {
  const { authEmail, authError } = useLoaderData<typeof loader>()
  const inputRef = useRef<HTMLInputElement>(null)
  const isHydrated = useHydrated()
  const navigation = useNavigation()
  const isSubmitting = navigation.state === 'submitting'
  const isPending = useIsPending()

  const [emailForm, { email }] = useForm({
    constraint: getZodConstraint(LoginSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LoginSchema })
    },
  })

  useEffect(() => {
    isHydrated && inputRef.current?.focus()
  }, [isHydrated])

  return (
      <div className="flex flex-col items-center justify-center gap-6 p-10 w-full max-w-lg mx-auto h-screen">
        <h1 className="text-5xl font-semibold text-center">Welcome back!</h1>
        <p className="text-lg text-darkGray max-w-md text-center">
          NeighborCloud gives you the freedom and flexibility to efficiently manage your neighborhoods.
        </p>

      <Form
        method="POST"
        autoComplete="off"
        className="flex w-full flex-col items-center gap-1 max-w-sm mx-auto"
        {...getFormProps(emailForm)}>
        {/* Security */}
        <AuthenticityTokenInput />
        <HoneypotInputs />

        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <Input
            placeholder="Email"
            ref={inputRef}
            defaultValue={authEmail ? authEmail : ''}
            className={`bg-transparent ${
              email.errors && 'border-destructive focus-visible:ring-destructive'
            }`}
            {...getInputProps(email, { type: 'email' })}
          />
        </div>

        <div className="flex flex-col w-full">
          {!authError && email.errors && (
            <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
              {email.errors.join(' ')}
            </span>
          )}
          {!authEmail && authError && (
            <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
              {authError.message}
            </span>
          )}
        </div>

        <Button type="submit" className="w-full">
          {isSubmitting ? <Loader2 className="animate-spin" /> : 'Continue with Email'}
        </Button>
      </Form>

      <div className="relative flex w-full items-center justify-center">
        <span className="absolute w-full border-b border-border" />
        <span className="z-10 bg-card px-2 text-xs font-medium uppercase text-primary/60">
          OR
        </span>
      </div>

      <Form method="POST" action="/auth/google">
        <Button type="submit" className="w-full">
          {isSubmitting ? <Loader2 className="animate-spin" /> : 'Sign in with Google'}
        </Button>
      </Form>

      <p className="px-12 text-center text-sm font-normal leading-normal text-primary/60">
        By signing in, you agree to our<br />
        <a href="../tos" className="underline hover:text-primary">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="../privacy" className="underline hover:text-primary">
          Privacy Policy.
        </a>
      </p>
      </div>
  )
}
